import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  Snackbar,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Typography,
  useTheme
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../../components/Dashboard/Header";
import { tokens } from "../../../theme";
import axios from "axios";
import { schemeCategory10 } from "d3-scale-chromatic";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

// Shared TextField styles
const textFieldStyles = {
  "& .MuiOutlinedInput-root": {
    fontSize: "0.9rem",
    height: "45px",
    "& fieldset": { borderColor: "#771726" },
    "&:hover fieldset": { borderColor: "#771726" },
    "&.Mui-focused fieldset": { borderColor: "#771726" },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.85rem",
    color: "#000000",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#000000",
  },
};

const nowLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmtNoSeconds = (v) =>
  typeof v === "string" ? v.replace(/:\d{2}$/, "") : v;

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1, bgcolor: "#222", border: "1px solid #555" }}>
      <Typography variant="caption" sx={{ color: "#ddd" }}>
        {fmtNoSeconds(label)}
      </Typography>
      {payload.map((entry) => (
        <Typography key={entry.dataKey} variant="body2" sx={{ color: entry.color }}>
          {entry.name || entry.dataKey}: {entry.value}
        </Typography>
      ))}
    </Paper>
  );
}

const seriesOptions = [
    // Analógicas
    { key: "tempEvaporadorSalida", label: "Temp Evaporador Salida" },
    { key: "tempDescarga", label: "Temp Descarga" },
    { key: "presionSuccion", label: "Presión Succión" },
    { key: "presionDescarga", label: "Presión Descarga" },
    { key: "porcVelVentECGrupo1", label: "Porcentaje Velocidad Ventilador EC Grupo 1" },
    { key: "porcVelVentECGrupo2", label: "Porcentaje Velocidad Ventilador EC Grupo 2" },
    { key: "voltaje", label: "Voltaje" },
    { key: "amperaje", label: "Amperaje" },
    { key: "sobrecalentamiento", label: "Sobrecalentamiento" },
    { key: "tempSuccion", label: "Temp Succión" },
    { key: "condicionActual", label: "Condición Actual" },

    // Digitales
    { key: "controlCarga", label: "Control de Carga" },
    { key: "ramploading", label: "Ramploading" },
    { key: "interlockComp1", label: "Interlock Comp 1" },
    { key: "statusMotorProtect1", label: "Status MotorProtect 1" },
    { key: "nivelAceiteCarter", label: "Nivel de Aceite Carter" },
    { key: "switchFlujoAguaHelada", label: "Switch Flujo Agua Helada" },
    { key: "protectorBajaPresion", label: "Protector Baja Presión" },
    { key: "encendidoCompresor1", label: "Encendido Compresor 1" },
    { key: "load", label: "Load" },
    { key: "unload", label: "Unload" },
    { key: "activaExv", label: "Activa EXV" },
    { key: "solenoideInyeccionSuccion", label: "Solenoide Inyección Succión" },
    { key: "ventiladorGrupo1EC", label: "Ventilador Grupo 1 EC" },
    { key: "ventiladorGrupo2AC", label: "Ventilador Grupo 2 AC" },
  ];

const GFSCV21Comp = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [idEquipo, setIdEquipo] = useState("");
  const [fechaInicio, setFechaInicio] = useState(nowLocal());
  const [fechaFinal, setFechaFinal] = useState(nowLocal());

  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = (_, newIndex) => setTabIndex(newIndex);

  const [rows, setRows] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE;
  const MACHINE = "Full Service Cooling V2 1Comp";
  const QUERY = "Grafica";

  const toggleKey = (k) => {
    setSelectedKeys((prev) =>
      prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]
    );
  };

  const fetchRows = async () => {
    if (new Date(fechaFinal) < new Date(fechaInicio)) {
      setError("La fecha final no puede ser anterior a la fecha de inicio.");
      return;
    }

    setError(null);
    setLoading(true);
    setRows([]);

    try {
      const res = await axios.post(
        `${API_BASE}/test/consulta/${encodeURIComponent(MACHINE)}/${encodeURIComponent(QUERY)}`,
        { idEquipo, fechaInicio, fechaFinal },
        { headers: { "Content-Type": "application/json" } }
      );

      const body = res.data.body
        ? (typeof res.data.body === "string"
            ? JSON.parse(res.data.body)
            : res.data.body)
        : res.data;

      const extractedData = Array.isArray(body.data)
        ? body.data
        : (Array.isArray(body.data?.[0]) ? body.data[0] : []);

      const normalizeKeys = (row) => {
        const clean = {};
        for (const k in row) clean[k.trim()] = row[k];
        return clean;
      };

      const parseValue = (v) => {
        if (v === null || v === undefined || v === "") return null;
        const s = String(v).trim().toLowerCase();
        if (["true", "on", "encendido", "sí", "si", "1"].includes(s)) return 1;
        if (["false", "off", "apagado", "no", "0"].includes(s)) return 0;
        const n = Number(s.replace(",", "."));
        return Number.isFinite(n) ? n : v;
      };

      const mapped = extractedData.map((row, i) => {
        const r = normalizeKeys(row);
        const fechaUTC = new Date(r["Fecha"]);
        const ts = fechaUTC.getTime();

        const formattedDate =
          `${fechaUTC.getUTCFullYear()}-${String(fechaUTC.getUTCMonth() + 1).padStart(2, "0")}` +
          `-${String(fechaUTC.getUTCDate()).padStart(2, "0")} ` +
          `${String(fechaUTC.getUTCHours()).padStart(2, "0")}:` +
          `${String(fechaUTC.getUTCMinutes()).padStart(2, "0")}:` +
          `${String(fechaUTC.getUTCSeconds()).padStart(2, "0")}`;

        const base = {
          id: i + 1,
          ts,
          tipoMaquinaControl: r["Tipo de Máquina y Control"],
          numeroEquipo: r["Número de Equipo"],
          fecha: formattedDate,

          // Analógicas
          tempEvaporadorSalida: r["Temp Evaporador Salida"],
          tempDescarga: r["Temp Descarga"],
          presionSuccion: r["Presión Succión"],
          presionDescarga: r["Presión Descarga"],
          porcVelVentECGrupo1: r["Porcentaje Velocidad Ventilador EC Grupo 1"],
          porcVelVentECGrupo2: r["Porcentaje Velocidad Ventilador EC Grupo 2"],
          voltaje: r["Voltaje"],
          amperaje: r["Amperaje"],
          sobrecalentamiento: r["Sobrecalentamiento"],
          tempSuccion: r["Temp Succión"],
          condicionActual: r["Condición Actual"],

          // Digitales
          controlCarga: r["Control de Carga"],
          ramploading: r["Ramploading"],
          interlockComp1: r["Interlock Comp 1"],
          statusMotorProtect1: r["Status MotorProtect 1"],
          nivelAceiteCarter: r["Nivel de Aceite Carter"],
          switchFlujoAguaHelada: r["Switch Flujo Agua Helada"],
          protectorBajaPresion: r["Protector Baja Presión"],
          encendidoCompresor1: r["Encendido Compresor 1"],
          load: r["Load"],
          unload: r["Unload"],
          activaExv: r["Activa EXV"],
          solenoideInyeccionSuccion: r["Solenoide Inyección Succión"],
          ventiladorGrupo1EC: r["Ventilador Grupo 1 EC"],
          ventiladorGrupo2AC: r["Ventilador Grupo 2 AC"],
        };

        const TEXT_KEYS = new Set(["id", "tipoMaquinaControl", "numeroEquipo", "fecha", "ts"]);
        Object.keys(base).forEach((k) => {
          if (!TEXT_KEYS.has(k)) base[k] = parseValue(base[k]);
        });

        return base;
      });

      mapped.sort((a, b) => {
        const ta = a.ts ?? Date.parse(a.fecha) ?? 0;
        const tb = b.ts ?? Date.parse(b.fecha) ?? 0;
        return ta - tb;
      });

      setRows(mapped);
    } catch (err) {
      setError("Error al consultar los datos. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const analogKeys = [
    "tempEvaporadorSalida",
    "tempDescarga",
    "presionSuccion",
    "presionDescarga",
    "porcVelVentECGrupo1",
    "porcVelVentECGrupo2",
    "voltaje",
    "amperaje",
    "sobrecalentamiento",
    "tempSuccion",
    "condicionActual",
  ];

  const digitalKeys = [
    "controlCarga",
    "ramploading",
    "interlockComp1",
    "statusMotorProtect1",
    "nivelAceiteCarter",
    "switchFlujoAguaHelada",
    "protectorBajaPresion",
    "encendidoCompresor1",
    "load",
    "unload",
    "activaExv",
    "solenoideInyeccionSuccion",
    "ventiladorGrupo1EC",
    "ventiladorGrupo2AC",
  ];

  const columns = [
    { field: "id", headerName: "ID", width: 90 },

    { field: "tipoMaquinaControl", headerName: "Tipo de Máquina y Control", minWidth: 220, flex: 1 },
    { field: "numeroEquipo", headerName: "Número de Equipo", minWidth: 160, flex: 1 },
    { field: "fecha", headerName: "Fecha", minWidth: 180, flex: 1 },

    // Analógicas
    { field: "tempEvaporadorSalida", headerName: "Temp Evaporador Salida", minWidth: 200, flex: 1 },
    { field: "tempDescarga", headerName: "Temp Descarga", minWidth: 180, flex: 1 },
    { field: "presionSuccion", headerName: "Presión Succión", minWidth: 180, flex: 1 },
    { field: "presionDescarga", headerName: "Presión Descarga", minWidth: 180, flex: 1 },
    { field: "porcVelVentECGrupo1", headerName: "Porcentaje Velocidad Ventilador EC Grupo 1", minWidth: 280, flex: 1 },
    { field: "porcVelVentECGrupo2", headerName: "Porcentaje Velocidad Ventilador EC Grupo 2", minWidth: 280, flex: 1 },
    { field: "voltaje", headerName: "Voltaje", minWidth: 140, flex: 1 },
    { field: "amperaje", headerName: "Amperaje", minWidth: 140, flex: 1 },
    { field: "sobrecalentamiento", headerName: "Sobrecalentamiento", minWidth: 180, flex: 1 },
    { field: "tempSuccion", headerName: "Temp Succión", minWidth: 160, flex: 1 },
    { field: "condicionActual", headerName: "Condición Actual", minWidth: 180, flex: 1 },

    // Digitales
    { field: "controlCarga", headerName: "Control de Carga", minWidth: 180, flex: 1 },
    { field: "ramploading", headerName: "Ramploading", minWidth: 160, flex: 1 },
    { field: "interlockComp1", headerName: "Interlock Comp 1", minWidth: 180, flex: 1 },
    { field: "statusMotorProtect1", headerName: "Status MotorProtect 1", minWidth: 200, flex: 1 },
    { field: "nivelAceiteCarter", headerName: "Nivel de Aceite Carter", minWidth: 200, flex: 1 },
    { field: "switchFlujoAguaHelada", headerName: "Switch Flujo Agua Helada", minWidth: 210, flex: 1 },
    { field: "protectorBajaPresion", headerName: "Protector Baja Presión", minWidth: 190, flex: 1 },
    { field: "encendidoCompresor1", headerName: "Encendido Compresor 1", minWidth: 200, flex: 1 },
    { field: "load", headerName: "Load", minWidth: 140, flex: 1 },
    { field: "unload", headerName: "Unload", minWidth: 140, flex: 1 },
    { field: "activaExv", headerName: "Activa EXV", minWidth: 160, flex: 1 },
    { field: "solenoideInyeccionSuccion", headerName: "Solenoide Inyección Succión", minWidth: 230, flex: 1 },
    { field: "ventiladorGrupo1EC", headerName: "Ventilador Grupo 1 EC", minWidth: 200, flex: 1 },
    { field: "ventiladorGrupo2AC", headerName: "Ventilador Grupo 2 AC", minWidth: 200, flex: 1 },
  ];

  const dummyData = [
    { fecha: "1", value: 2 },
    { fecha: "2", value: 5.5 },
    { fecha: "3", value: 2 },
    { fecha: "5", value: 8.5 },
    { fecha: "8", value: 1.5 },
    { fecha: "10", value: 5 },
  ];

  const displayData = rows.length ? rows : dummyData;

  const seriesKeys = React.useMemo(() => {
    const displaySeriesKeys = seriesOptions
      .filter((opt) => selectedKeys.includes(opt.key))
      .map((opt) => opt.key);

    return displaySeriesKeys.length
      ? displaySeriesKeys
      : (rows.length ? [] : ["value"]);
  }, [selectedKeys, rows.length]);

  const lineColors = React.useMemo(
    () => seriesKeys.map((_, i) => schemeCategory10[i % schemeCategory10.length]),
    [seriesKeys]
  );

  const labelMap = React.useMemo(() => {
    const m = Object.fromEntries(seriesOptions.map((o) => [o.key, o.label]));
    m.value = "Valor (demo)";
    return m;
  }, []);

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", mt: "90px", px: 2 }}>
      <Header
        title="Full Service Cooling V2 1Comp"
        sxTitle={{ fontSize: "2rem", textAlign: "center" }}
      />

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <TextField
              label="ID Equipo"
              variant="outlined"
              fullWidth
              value={idEquipo}
              onChange={(e) => setIdEquipo(e.target.value)}
              sx={textFieldStyles}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <TextField
              label="Fecha Inicio"
              variant="outlined"
              type="datetime-local"
              fullWidth
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={textFieldStyles}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <TextField
              label="Fecha Final"
              variant="outlined"
              type="datetime-local"
              fullWidth
              value={fechaFinal}
              onChange={(e) => setFechaFinal(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={textFieldStyles}
            />
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2, height: 400, display: "flex", flexDirection: "column" }}>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ mb: 1 }}
            >
              <Tab label="Análogos" />
              <Tab label="Digitales" />
            </Tabs>

            {tabIndex === 0 && (
              <Box sx={{ flex: 1, overflowY: "auto", px: 1, "& .MuiFormControlLabel-root": { mb: 1 } }}>
                <Grid container spacing={1}>
                  {seriesOptions
                    .filter((opt) => analogKeys.includes(opt.key))
                    .map((opt) => (
                      <Grid item xs={6} key={opt.key}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedKeys.includes(opt.key)}
                              onChange={() => toggleKey(opt.key)}
                            />
                          }
                          label={
                            <Typography variant="caption" sx={{ fontSize: 12, fontWeight: "bold" }}>
                              {opt.label}
                            </Typography>
                          }
                        />
                      </Grid>
                    ))}
                </Grid>
              </Box>
            )}

            {tabIndex === 1 && (
              <Box sx={{ flex: 1, overflowY: "auto", px: 1, "& .MuiFormControlLabel-root": { mb: 1 } }}>
                <Grid container spacing={1}>
                  {seriesOptions
                    .filter((opt) => digitalKeys.includes(opt.key))
                    .map((opt) => (
                      <Grid item xs={6} key={opt.key}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedKeys.includes(opt.key)}
                              onChange={() => toggleKey(opt.key)}
                            />
                          }
                          label={<Typography variant="caption" fontWeight="bold">{opt.label}</Typography>}
                        />
                      </Grid>
                    ))}
                </Grid>
              </Box>
            )}

            <Button
              variant="contained"
              sx={{
                bgcolor: "#771726",
                color: "white",
                mt: 1,
                width: isMobile ? "100%" : "auto",
                "&:hover": { bgcolor: "#747274" },
              }}
              onClick={(e) => {
                e.preventDefault();
                fetchRows();
              }}
            >
              Graficar
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper
            elevation={1}
            sx={{
              height: 400,
              p: 2,
              display: "flex",
              flexDirection: "column"
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <DataGrid
                rows={rows}
                columns={columns}
                loading={loading}
                disableColumnMenu
                pageSize={10}
                sx={{
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#771726",
                    zIndex: 1,
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: "#771726",
                    color: "#ffffff",
                    borderTop: "none",
                  },
                  "& .MuiTablePagination-root, & .MuiSvgIcon-root": {
                    color: "#ffffff",
                  },
                }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Grid container mb={3}>
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ height: 400, p: 2, pb: 4 }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    overflowX: "auto",
                    whiteSpace: "nowrap",
                    mb: 1,
                    ml: "30px",
                    width: "calc(100% - 60px)",
                  }}
                >
                  {seriesKeys.map((key, i) => (
                    <Box key={key} sx={{ display: "flex", alignItems: "center", mr: 3 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: lineColors[i],
                          mr: 1,
                        }}
                      />
                      <Typography variant="caption">{labelMap[key]}</Typography>
                    </Box>
                  ))}
                </Box>

                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    animationDuration={0}
                    data={displayData}
                    margin={{ top: 20, right: 30, left: -30, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="4 4" stroke={colors.grey[700]} />

                    <XAxis
                      dataKey="fecha"
                      interval="auto"
                      minTickGap={70}
                      tickMargin={10}
                      tick={{ fill: colors.grey[300], fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: colors.grey[500] }}
                      tickFormatter={fmtNoSeconds}
                    />

                    <YAxis
                      tick={{ fill: colors.grey[300], fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: colors.grey[500] }}
                      domain={["auto", "auto"]}
                    />

                    <Tooltip
                      content={<CustomTooltip />}
                      delayShow={0}
                      cursor={{ stroke: colors.grey[500], strokeDasharray: "3 3" }}
                    />

                    {seriesKeys.map((key, i) => (
                      <Line
                        key={key}
                        dataKey={key}
                        name={labelMap[key]}
                        stroke={lineColors[i]}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 6 }}
                        isAnimationActive={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GFSCV21Comp;